import {indexBy, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {ChangeOrderReturnVehicleRequestBody, RentalOrderResponseBody} from '@dms/api/rental';
import {getLevelIndicatorFlags, LevelIndicator, VatRateOption} from '@dms/shared';

import {Nullish, parseDate} from 'shared/web';

import {serializeBatteryCharge} from '../../../utils/serializeBatteryCharge';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

// eslint-disable-next-line complexity -- Pre-existing violation
export const getReturnVehicleRequestBody = (
  data: ReturnVehicleFormValues,
  vatTypes: VatRateOption[],
  rentalOrder: RentalOrderResponseBody | Nullish,
  levelIndicators: LevelIndicator[]
): ChangeOrderReturnVehicleRequestBody => {
  const {hasFuel, hasBattery} = getLevelIndicatorFlags(levelIndicators);

  const vatRatesByValue = indexBy((item) => item.value, vatTypes);

  const getRateByType = (type: string | Nullish) => {
    if (isNil(type)) {
      return '0';
    }

    return vatRatesByValue?.[type ?? '']?.rate ?? vatRatesByValue?.S?.rate ?? '0';
  };

  const customRentPrice =
    data.isCustomPriceEnabled &&
    data.customPrice?.withoutVat &&
    data.customPrice?.withVat &&
    data.customPrice?.vat &&
    data.customPrice?.vatType
      ? {
          withoutVat: data.customPrice.withoutVat.toString(),
          vat: data.customPrice.vat.toString(),
          withVat: data.customPrice.withVat.toString(),
          vatRate: getRateByType(data.customPrice.vatType),
          vatType: data.customPrice.vatType,
        }
      : null;

  return {
    actualMileage: data.actualMileage,
    authorizedPersons: data.authorizedPersons.map((item) => ({
      ...item,
      idCardNumber: isNilOrEmpty(item.idCardNumber) ? null : item.idCardNumber,
      drivingLicenceNumber: isNilOrEmpty(item.drivingLicenceNumber)
        ? null
        : item.drivingLicenceNumber,
      phoneNumber:
        isNil(item.phoneNumber) || isNilOrEmpty(item.phoneNumber?.number) ? null : item.phoneNumber,
    })),
    customRentPrice,
    equipment: data.equipment,
    fuelTank: hasFuel ? data.fuelTank.toString() : null,
    batteryCharge: hasBattery ? serializeBatteryCharge(data.batteryCharge) : null,
    rentAddress: data.rentAddress,
    rentalPeriod: {
      from: parseDate(rentalOrder?.plannedRentalPeriod?.from)?.toISOString() ?? '',
      to: data.actualReturnDate.toISOString(),
    },
    returnAddress: data.isAddressSame ? null : data.returnAddress || null,
    rentPrice: {
      dailyRate: data.rentPrice.dailyRate.toString(),
      paidDays: data.rentPrice.paidDays,
      freeDailyKilometers: data.rentPrice.freeDailyKilometers,
      totalFreeKilometers: data.rentPrice.totalFreeKilometers,
      coPayment: {
        withoutVat: data.rentPrice.coPayment?.withoutVat?.toString() || '0',
        vat: data.rentPrice.coPayment?.vat?.toString() || '0',
        withVat: data.rentPrice.coPayment?.withVat?.toString() || '0',
        vatRate: getRateByType(data.rentPrice.coPayment?.vatType),
        vatType: data.rentPrice.coPayment?.vatType ?? 'S',
      },
      contractPenalty: {
        withoutVat: data.rentPrice.contractPenalty?.withoutVat?.toString() || '0',
        vat: data.rentPrice.contractPenalty?.vat?.toString() || '0',
        withVat: data.rentPrice.contractPenalty?.withVat?.toString() || '0',
        vatRate: getRateByType(data.rentPrice.contractPenalty?.vatType),
        vatType: data.rentPrice.contractPenalty?.vatType ?? 'S',
      },
      rentalPrice: {
        withoutVat: data.rentPrice.rentalPrice.withoutVat.toString(),
        vat: data.rentPrice.rentalPrice.vat.toString(),
        withVat: data.rentPrice.rentalPrice.withVat.toString(),
        vatRate: getRateByType(data.rentPrice.rentalPrice.vatType),
        vatType: data.rentPrice.rentalPrice.vatType,
      },
      pricePerKilometer: {
        withoutVat: data.rentPrice.pricePerKilometer.withoutVat.toString(),
        vat: data.rentPrice.pricePerKilometer.vat.toString(),
        withVat: data.rentPrice.pricePerKilometer.withVat.toString(),
        vatRate: getRateByType(data.rentPrice.pricePerKilometer.vatType),
        vatType: data.rentPrice.pricePerKilometer.vatType ?? 'S',
      },
      deliveryPrice: {
        withoutVat: data.rentPrice.deliveryPrice?.withoutVat?.toString() || '0',
        vat: data.rentPrice.deliveryPrice?.vat?.toString() || '0',
        withVat: data.rentPrice.deliveryPrice?.withVat?.toString() || '0',
        vatRate: getRateByType(data.rentPrice.deliveryPrice?.vatType),
        vatType: data.rentPrice.deliveryPrice?.vatType ?? 'S',
      },
      otherPrice: {
        withoutVat: data.rentPrice.otherPrice?.withoutVat?.toString() || '0',
        vat: data.rentPrice.otherPrice?.vat?.toString() || '0',
        withVat: data.rentPrice.otherPrice?.withVat?.toString() || '0',
        vatRate: getRateByType(data.rentPrice.otherPrice?.vatType),
        vatType: data.rentPrice.otherPrice?.vatType ?? 'S',
      },
      discount: {
        percent: data.rentPrice.discount?.percent?.toString() || '0',
        discount: {
          withoutVat: data.rentPrice.discount?.discount?.withoutVat?.toString() || '0',
          vat: data.rentPrice.discount?.discount?.vat?.toString() || '0',
          withVat: data.rentPrice.discount?.discount?.withVat?.toString() || '0',
          vatRate: getRateByType(data.rentPrice.discount?.discount?.vatType),
          vatType: data.rentPrice.discount?.discount?.vatType ?? 'S',
        },
      },
      chargedFuel: {
        amount: data.rentPrice.chargedFuel?.amount?.toString() || '0',
        price: {
          withoutVat: data.rentPrice.chargedFuel?.price?.withoutVat?.toString() || '0',
          vat: data.rentPrice.chargedFuel?.price?.vat?.toString() || '0',
          withVat: data.rentPrice.chargedFuel?.price?.withVat?.toString() || '0',
          vatRate: getRateByType(data.rentPrice.chargedFuel?.price?.vatType),
          vatType: data.rentPrice.chargedFuel?.price?.vatType ?? 'S',
        },
      },
      chargedKilometer: {
        amount: data.rentPrice.chargedKilometer?.amount?.toString() || '0',
        price: {
          withoutVat: data.rentPrice.chargedKilometer?.price?.withoutVat?.toString() || '0',
          vat: data.rentPrice.chargedKilometer?.price?.vat?.toString() || '0',
          withVat: data.rentPrice.chargedKilometer?.price?.withVat?.toString() || '0',
          vatRate: getRateByType(data.rentPrice.chargedKilometer?.price?.vatType),
          vatType: data.rentPrice.chargedKilometer?.price?.vatType ?? 'S',
        },
      },
    },
    rentType: data.rentType,
  };
};
